import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useStore } from "../context/Store";
import { PATTERNS, t } from "../i18n";
import ProductCard from "../components/ProductCard";
import Seo, { JsonLd } from "../components/Seo";
import { trackSearch } from "../components/MarketingLoader";

export const CitiesList = () => {
  const { locale = "en" } = useParams();
  const { boot } = useStore();
  const pat = PATTERNS[locale];
  const cityList = boot?.cities || [];
  return (
    <div className="max-w-content mx-auto px-6 py-16" data-testid="cities-list">
      <Seo title={`${t(locale, "cities")} | Fleurinelle`} description={boot?.editorial?.description || t(locale, "tagline")} path={`/${locale}/cities`} />
      {cityList.length ? (
        <JsonLd data={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: t(locale, "cities"),
          numberOfItems: cityList.length,
          itemListElement: cityList.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${window.location.origin}/${locale}/${pat.city}/${c.slug}`,
            name: c.name,
          })),
        }} />
      ) : null}
      <h1 className="font-display text-4xl text-ink mb-10">{t(locale, "cities")}</h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-8">
        {cityList.map((c) => (
          <Link key={c.id} to={`/${locale}/${pat.city}/${c.slug}`} data-testid={`cities-list-${c.key}`} className="group">
            <div className="overflow-hidden">
              <img src={c.hero_image_url} alt={c.name} className="w-full h-[260px] object-cover scale-100 group-hover:scale-[1.03] transition-transform duration-500 ease-out" />
            </div>
            <h2 className="font-display text-2xl text-ink mt-3">{c.name}</h2>
            <p className="text-sm text-muted">{t(locale, "sameDay")} · {c.same_day_cutoff}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const OccasionsList = () => {
  const { locale = "en" } = useParams();
  const { boot } = useStore();
  const pat = PATTERNS[locale];
  const listSlug = (PATTERNS[locale] || PATTERNS.en).occasion;
  const occasionList = boot?.occasions || [];
  const groups = occasionList.reduce((acc, o) => {
    (acc[o.category] = acc[o.category] || []).push(o);
    return acc;
  }, {});
  return (
    <div className="max-w-content mx-auto px-6 py-16" data-testid="occasions-list">
      <Seo title={`${t(locale, "occasions")} | Fleurinelle`} description={boot?.editorial?.description || t(locale, "tagline")} path={`/${locale}/${listSlug}`}
           alternates={Object.fromEntries(Object.keys(PATTERNS).map((l) => [l, `/${l}/${PATTERNS[l].occasion}`]))} />
      {occasionList.length ? (
        <JsonLd data={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: t(locale, "occasions"),
          numberOfItems: occasionList.length,
          itemListElement: occasionList.map((o, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${window.location.origin}/${locale}/${pat.occasion}/${o.slug}`,
            name: o.name,
          })),
        }} />
      ) : null}
      <h1 className="font-display text-4xl text-ink mb-10">{t(locale, "occasions")}</h1>
      <div className="space-y-12">
        {Object.entries(groups).map(([cat, items]) => (
          <section key={cat}>
            <h2 className="smallcaps mb-4">{cat}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {items.map((o) => (
                <Link key={o.id} to={`/${locale}/${pat.occasion}/${o.slug}`} data-testid={`occasions-list-${o.key}`} className="group">
                  <div className="overflow-hidden">
                    <img src={o.hero_image_url} alt={o.name} loading="lazy" className="w-full aspect-[4/5] object-cover scale-100 group-hover:scale-[1.03] transition-transform duration-500 ease-out" />
                  </div>
                  <p className="text-sm text-ink mt-2">{o.name}</p>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export const GuidesList = () => {
  const { locale = "en" } = useParams();
  const pat = PATTERNS[locale];
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/articles", { params: { locale } }).then((r) => setItems(r.data));
  }, [locale]);
  return (
    <div className="max-w-content mx-auto px-6 py-16" data-testid="guides-list">
      <Seo title={`${t(locale, "guides")} | Fleurinelle`} description={t(locale, "tagline")} path={`/${locale}/guides`} />
      {items.length ? (
        <JsonLd data={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: t(locale, "guides"),
          numberOfItems: items.length,
          itemListElement: items.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${window.location.origin}/${locale}/${pat.guide}/${a.slug}`,
            name: a.title,
          })),
        }} />
      ) : null}
      <h1 className="font-display text-4xl text-ink mb-10">{t(locale, "guides")}</h1>
      <div className="grid md:grid-cols-3 gap-8">
        {items.map((a) => (
          <Link key={a.id} to={`/${locale}/${pat.guide}/${a.slug}`} className="group" data-testid={`guide-${a.slug}`}>
            <div className="overflow-hidden">
              <img src={a.hero_image_url} alt={a.title} loading="lazy" className="w-full h-[220px] object-cover scale-100 group-hover:scale-[1.03] transition-transform duration-500 ease-out" />
            </div>
            <h2 className="font-display text-xl text-ink mt-3">{a.title}</h2>
            <p className="text-sm text-muted mt-1">{a.excerpt}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const SearchPage = () => {
  const { locale = "en" } = useParams();
  const { currency } = useStore();
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [data, setData] = useState(null);
  const trackedQuery = useRef("");
  useEffect(() => {
    api.get("/search", { params: { q, locale, currency } }).then((r) => {
      setData(r.data);
      if (q && trackedQuery.current !== q) { trackedQuery.current = q; trackSearch(q, r.data.products || [], currency); }
    });
  }, [q, locale, currency]);
  return (
    <div className="max-w-content mx-auto px-6 py-16" data-testid="search-page">
      <Seo title={`${t(locale, "search")}: ${q} | Fleurinelle`} noindex />
      <h1 className="font-display text-3xl text-ink mb-8">{t(locale, "search")}: {q}</h1>
      {data && !data.products.length ? <p className="text-muted">{t(locale, "noResults")}</p> : null}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {data?.products?.map((p, i) => (
          <ProductCard key={p.id} product={p} locale={locale} index={i} />
        ))}
      </div>
    </div>
  );
};
